import { useTranslation } from "react-i18next";
import { Box, Button, Text, Flex } from "@chakra-ui/react";
import { ArrowForwardIcon } from "@chakra-ui/icons";
import { CheckIcon } from "@chakra-ui/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useCourseManagementContext } from "@root/context/course-management-context";
import { stepperSteps } from "@root/constants";
import CourseStep1 from "@root/components/kpec-admin/course-management/CourseStep1";
import CourseStep2 from "../../../components/kpec-admin/course-management/CourseStep2";
import CourseStep3 from "../../../components/kpec-admin/course-management/CourseStep3";
import { useEffect, useState } from "react";

const CourseCreate = () => {
  const { t } = useTranslation("common");
  const {
    handleSubmit1,
    addCourseLoading,
    updateCourseLoading,
    onStep1Submit,
    step1Data,
    step2Data,
    step3Data,
    methods3,
    onStep3Submit,
    currentStep,
    isStep3Disabled,
    onStep2Submit,
    isStep2Disabled,
    fetchCourse,
    setCourseData,
    setUpdateCourseId,
    resetData,
    setCurrentStep,
    uploadProgress,
    isUploadingChunks,
    isUploading,
    addVideoLoading,
    updateVideoLoading,
    duration
  } = useCourseManagementContext();
  const navigate = useNavigate();
  const { id } = useParams();

  const { handleSubmit: handleSubmit3 } = methods3;

  const handleSaveAndContinue = () => {
    if (currentStep === 0) {
      handleSubmit1(onStep1Submit)();
    } else if (currentStep === 1) {
      onStep2Submit();
    } else if (currentStep === 2) {
      handleSubmit3(onStep3Submit)();
    }
  };

  const Stepper = () => (
    <Flex align="center" justify="center" mb={8} mt={2}>
      {stepperSteps()?.map((step, idx) => {
        const isCompleted = idx < currentStep;
        const isCurrent = idx === currentStep;
        return (
          <Flex align="center" key={step.label}>
            {/* Step Circle */}
            {isCompleted ? (
              <Flex
                align="center"
                justify="center"
                borderRadius="full"
                bg="#1FC77E"
                color="#fff"
                fontWeight={700}
                fontSize="20px"
                w="36px"
                h="36px"
              >
                <CheckIcon boxSize={5} />
              </Flex>
            ) : (
              <Flex
                align="center"
                justify="center"
                borderRadius="full"
                border="1px solid #B5C3CE"
                bg={isCurrent ? "#fff" : "#F7FAFC"}
                color={isCurrent ? "#1819B4" : "#798692"}
                fontWeight={isCurrent ? 700 : 500}
                fontSize="20px"
                w="36px"
                h="36px"
              >
                {idx + 1}
              </Flex>
            )}
            {/* Step Label */}
            <Text
              ml={3}
              mr={3}
              color={
                isCompleted ? "#1FC77E" : isCurrent ? "#1819B4" : "#798692"
              }
              fontWeight={isCompleted || isCurrent ? 600 : 500}
              fontSize="14px"
            >
              {step.label}
            </Text>
            {/* Dashed Line */}
            {idx < stepperSteps.length - 1 && (
              <Box
                as="span"
                mx={2}
                w="60px"
                h="2px"
                borderBottom="2px dashed #B5C3CE"
                display="inline-block"
                position="relative"
                top="-2px"
              />
            )}
          </Flex>
        );
      })}
    </Flex>
  );

  const renderSteps = () => {
    if (currentStep === 0) return <CourseStep1 defaultValues={step1Data} />;
    if (currentStep === 1) return <CourseStep2 defaultValues={step2Data} />;
    if (currentStep === 2) return <CourseStep3 defaultValues={step3Data} />;
  };

  useEffect(() => {
    if (id) {
      fetchCourse(id);
      setUpdateCourseId(Number(id));
    }
    return () => {
      resetData();
    };
  }, [id]);

  useEffect(() => {
    // Reset form data when component mounts if no ID is present (new course)
    if (!id) {
      resetData();
      setCourseData(null);
      setUpdateCourseId(null);
      setCurrentStep(0);
    }

    // Reset edit data when main parent edit component unmounts
    return () => {
      resetData(); // Also reset on unmount
      setCourseData(null);
      setUpdateCourseId(null);
    };
  }, [id]);

  return (
    <Box height={"100%"} display={"flex"} flexDir={"column"} m={5} p={2}>
      <Box flex={1} overflowY={"scroll"} paddingBottom={"50px"}>
        <Stepper />
        {renderSteps()}
      </Box>
      <Flex
        justifyContent={"space-between"}
        py={"10px"}
        bgColor={"white"}
        m={"-20px -30px"}
        p={"20px 30px"}
        borderTop={"1px solid #CDD6DC"}
        zIndex={1}
      >
        <Button
          type="button"
          size="lg"
          alignSelf="flex-end"
          bgColor={"white"}
          color={"#1819B4"}
          border={"1px solid #1819B4"}
          fontWeight={500}
          fontSize={"14px"}
          onClick={() => {
            if (currentStep > 0) {
              setCurrentStep((prev: any) => prev - 1);
            } else {
              navigate(-1); // if on first step, go back to previous page
            }
          }}
        >
          {currentStep == 0
            ? t("courseCreate.cancel")
            : t("courseCreate.previous")}
        </Button>

        <Button
          type="button"
          size="lg"
          alignSelf="flex-end"
          bgColor={"#1819B4"}
          fontSize={"14px"}
          fontWeight={500}
          onClick={handleSaveAndContinue}
          isLoading={addCourseLoading || updateCourseLoading}
          isDisabled={
            (currentStep === 1 && isStep2Disabled) ||
            (currentStep === 2 && isStep3Disabled && !id) ||
            (currentStep === 1 &&
              (isUploadingChunks ||
                addVideoLoading ||
                updateVideoLoading ||
                isUploading || duration))
          }
        >
          {currentStep === 2
            ? t("courseCreate.publishCourse")
            : t("courseCreate.saveAndContinue")}
          <ArrowForwardIcon ml={3} />
        </Button>
      </Flex>
    </Box>
  );
};

export default CourseCreate;
